import { Component, EventEmitter, inject, Input, Output, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { AngularSvgIconModule } from "angular-svg-icon";
import { TranslatePipe } from "../../pipe/translation.filter.pipe";
import { TableModule } from "primeng/table";
import { TooltipModule } from "primeng/tooltip";
import { ApeButtonComponent } from "../ape-button/ape-button.component";
import { ApeStatusBadgeComponent } from "../ape-status-badge/ape-status-badge.component";
import { TRANSLATION_KEYS } from "../../constants/translations/translation-keys";
import { MonitoringUserSummaryExtended, MonitoringUserSummaryIdsExtended } from "src/app/services/api/monitoring-backoffice-api.service";
import { ApeAvatarComponent } from "../ape-avatar/ape-avatar.component";
import { Router } from "@angular/router";
import { LoggerService } from "src/ancestors/logger.service";
import { ApeMenuComponent } from "../ape-menu/ape-menu.component";
import { MenuItem } from "primeng/api";
import { Menu } from "primeng/menu";
import { TranslateService } from "src/app/util/translate.service";
import { MonitoringEvaluationPhaseStatusEnumTypes, MonitoringSelfEvaluationPhaseStatusEnumTypes } from "alloy-performance-engine-cl";

@Component({
  selector: "ape-admin-monitoring-users-table",
  templateUrl: "./ape-admin-monitoring-users-table.component.html",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    AngularSvgIconModule,
    TranslatePipe,
    TableModule,
    TooltipModule,
    ApeButtonComponent,
    ApeStatusBadgeComponent,
    ApeAvatarComponent,
    ApeMenuComponent
  ]
})
export class ApeAdminMonitoringUsersTableComponent {
  /**
   * Chiavi di traduzione per type safety
   */
  readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  private logger = inject(LoggerService);

  /**
   * Se mostrare le due colonne del Feedback 360 (stato e accesso alla lista da validare)
   *
   * Il modulo Valutazione di Pixart non prevede il Feedback 360: le colonne restano nel markup per
   * i processi che lo usano, ma di default non si vedono (spec 7.0, elenco delle colonne).
   */
  @Input() showFeedback360Columns: boolean = false;

  @Input() usersData: MonitoringUserSummaryExtended[] = [];

  @Input() allUsersChecked: boolean = false;

  @Input() allUsersData: MonitoringUserSummaryIdsExtended[] = [];

  /**
   * Riferimento al menu tiered
   */
  @ViewChild("apeMenu") apeMenu!: Menu;

  /**
   * Menu items per le azioni
   */
  menuItems: MenuItem[] = [];

  @Input() processId: string | null = null;

  @Output() accessProcess = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onAllUsersCheckedChange = new EventEmitter<boolean>();

  @Output() onReopenEvaluation = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onReopenSelfEvaluation = new EventEmitter<MonitoringUserSummaryExtended>();

  /** RF-303 - riporta in calibrazione una scheda calibrata e non ancora condivisa */
  @Output() onReopenCalibratedScorecard = new EventEmitter<MonitoringUserSummaryExtended>();

  /** RF-303 - riporta allo step di Delivery una scheda già condivisa al collaboratore */
  @Output() onReopenSharedScorecard = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onSolicitEvaluation = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onSolicitSelfEvaluation = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() impersonateUser = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() impersonateManager = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onDownloadEvaluationCard = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onSendCalibrationCards = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onGoToHandleListFeedback360 = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onGoToEvaluationCards = new EventEmitter<MonitoringUserSummaryExtended>();

  /** Spec §9.5 - Apre il drawer del Commento di HR della persona */
  @Output() onOpenHrComment = new EventEmitter<MonitoringUserSummaryExtended>();

  private router = inject(Router);

  private translateService = inject(TranslateService);

  getStatusKind(status: string) {
    switch (status) {
      case "TODO":
        return "in-progress";
      case "COMPLETED":
        return "completed";
      case "NOT_DONE":
        return "disable";
    }
    return "disable";
  }

  allUsersCheckedChange(): void {
    this.allUsersChecked = !this.allUsersChecked;
    this.onAllUsersCheckedChange.emit(this.allUsersChecked);
  }

  protected reopenEvaluation(user: MonitoringUserSummaryExtended) {
    if (!user) return;
    if (!user.canReopenEvaluation) return;
    this.logger.info(`Reopen evaluation for user ${user.userId}`);
    this.onReopenEvaluation.emit(user);
  }

  protected reopenCalibratedScorecard(user: MonitoringUserSummaryExtended) {
    if (!user) return;
    if (!user.canReopenCalibratedScorecard) return;
    this.logger.info(`Reopen calibrated scorecard for user ${user.userId}`);
    this.onReopenCalibratedScorecard.emit(user);
  }

  protected reopenSharedScorecard(user: MonitoringUserSummaryExtended) {
    if (!user) return;
    if (!user.canReopenSharedScorecard) return;
    this.logger.info(`Reopen shared scorecard for user ${user.userId}`);
    this.onReopenSharedScorecard.emit(user);
  }

  protected reopenSelfEvaluation(user: MonitoringUserSummaryExtended) {
    if (!user) return;
    if (!user.canReopenSelfEvaluation) return;
    this.logger.info(`Reopen self evaluation for user ${user.userId}`);
    this.onReopenSelfEvaluation.emit(user);
  }

  /**
   * La voce "Visualizza schede" e' abilitata se almeno una fra la scheda di autovalutazione e la
   * scheda di valutazione del manager risulta in stato Completato.
   * @param user La riga di monitoraggio su cui e' aperto il menu azioni
   * @returns True se almeno una delle due schede e' completata
   */
  private canViewEvaluationCards(user: MonitoringUserSummaryExtended): boolean {
    return user?.selfAssessmentStatus === MonitoringSelfEvaluationPhaseStatusEnumTypes.COMPLETED
      || user?.evaluationStatus === MonitoringEvaluationPhaseStatusEnumTypes.COMPLETED;
  }

  private canSolicitEvaluation(user: MonitoringUserSummaryExtended) {
    return user?.canSolicitEvaluation ?? false;
  }

  private canSolicitSelfEvaluation(user: MonitoringUserSummaryExtended) {
    return user?.canSolicitSelfEvaluation ?? false;
  }

  protected solicitEvaluation(user: MonitoringUserSummaryExtended) {
    if (!user) return;
    if (!this.canSolicitEvaluation(user)) return;
    this.logger.info(`Solicit evaluation for user ${user.userId}`);
    this.onSolicitEvaluation.emit(user);
  }

  protected solicitSelfEvaluation(user: MonitoringUserSummaryExtended) {
    if (!user) return;
    if (!this.canSolicitSelfEvaluation(user)) return;
    this.logger.info(`Solicit self evaluation for user ${user.userId}`);
    this.onSolicitSelfEvaluation.emit(user);
  }

  toggleActionColumn(user: MonitoringUserSummaryExtended, event: Event): void {
    this.menuItems = [];

    // Azione: Verifica e invia risultati
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.VERIFY_AND_SEND_RESULTS),
      icon: "assets/icons/user.svg",
      disabled: !user?.canConfirmAndSendCalibration,
      command: () => {
        this.sendCalibrationCards(user);
      }
    });

    // Azione: Compara team in matrice (CA-1). Spec §7: con la valutazione Completata, a processo
    // Attivo o Chiuso; in matrice entra solo chi l'ha completata
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.COMPARE_TEAM_IN_MATRIX),
      icon: "assets/icons/users-round.svg",
      disabled: !user?.canCompareInMatrix,
      command: () => {
        this.onGoToMatrix(user);
      }
    });

    // Azione: Commento di HR (spec §7 e §9.5). L'etichetta dice cosa si trova aprendo il drawer;
    // con il commento presente la voce e' sempre abilitata (almeno in consultazione), senza
    // commento solo se lo si puo' inserire
    this.menuItems.push({
      label: this.translateService.instant(this.getHrCommentMenuLabelKey(user)),
      icon: "assets/icons/message-circle-more.svg",
      disabled: !user?.hasHrComment && !user?.canEditHrComment,
      command: () => {
        this.openHrComment(user);
      }
    });

    // Azione: Impersona utente
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.impersonate.IMPERSONATE_USER),
      icon: "assets/icons/user.svg",
      disabled: !user?.canImpersonateUser,
      command: () => {
        this.onImpersonateUser(user);
      }
    });

    // Azione: Impersona manager
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.impersonate.IMPERSONATE_MANAGER),
      icon: "assets/icons/users.svg",
      disabled: !user?.canImpersonateManager,
      command: () => {
        this.onImpersonateManager(user);
      }
    });

    // Azione: Visualizza schede.
    // Abilitata se almeno una fra autovalutazione e valutazione manager risulta COMPLETED.
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.VIEW_EVALUATION_CARDS),
      icon: "assets/icons/eye.svg",
      disabled: !this.canViewEvaluationCards(user),
      command: () => {
        this.goToEvaluationCards(user);
      }
    });

    // Azione: Scarica scheda di valutazione.
    // SEMPRE abilitata, "indipendentemente dallo stato della scheda e del processo" (spec PDF
    // valutazione §2, chiude PA-28): su una scheda ancora da compilare il PDF esce con le sezioni
    // presenti e il placeholder dove il dato non c'e'.
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.DOWNLOAD_EVALUATION_CARD),
      icon: "assets/icons/download.svg",
      command: () => {
        this.downloadEvaluationCard(user);
      }
    });

    // Azione: Reapri autovalutazione
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.REOPEN_SELF_EVALUATION),
      icon: "assets/icons/wand.svg",
      disabled: !user?.canReopenSelfEvaluation,
      command: () => {
        this.reopenSelfEvaluation(user);
      }
    });

    // Azione: Reapri valutazione
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.REOPEN_EVALUATION),
      icon: "assets/icons/wand.svg",
      disabled: !user?.canReopenEvaluation,
      command: () => {
        this.reopenEvaluation(user);
      }
    });

    // RF-303 - Azione: Riapri scheda calibrata (torna in calibrazione, distinta dalla riapertura pre-calibrazione)
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.REOPEN_CALIBRATED_SCORECARD),
      icon: "assets/icons/wand.svg",
      disabled: !user?.canReopenCalibratedScorecard,
      command: () => {
        this.reopenCalibratedScorecard(user);
      }
    });

    // RF-303 - Azione: Riapri scheda condivisa (torna allo step di Delivery)
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.REOPEN_SHARED_SCORECARD),
      icon: "assets/icons/wand.svg",
      disabled: !user?.canReopenSharedScorecard,
      command: () => {
        this.reopenSharedScorecard(user);
      }
    });

    // Azione: Sollecito autovalutazione
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.SOLICIT_SELF_EVALUATION),
      icon: "assets/icons/mail.svg",
      disabled: !user?.canSolicitSelfEvaluation,
      command: () => {
        this.solicitSelfEvaluation(user);
      }
    });

    // Azione: Sollecito valutazione
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.SOLICIT_EVALUATION),
      icon: "assets/icons/mail.svg",
      disabled: !user?.canSolicitEvaluation,
      command: () => {
        this.solicitEvaluation(user);
      }
    });

    this.apeMenu.toggle(event);
  }

  /**
   * L'etichetta della voce Commento di HR (spec §7)
   * - "Commento di HR": commento non presente;
   * - "Modifica commento di HR": commento presente e modificabile;
   * - "Vedi commento di HR": commento presente e non modificabile.
   * @param user La persona
   * @returns La chiave i18n
   */
  private getHrCommentMenuLabelKey(user: MonitoringUserSummaryExtended): string {
    const detailsKeys = TRANSLATION_KEYS.admin.monitoring.details;
    if (!user?.hasHrComment) {
      return detailsKeys.HR_COMMENT;
    }
    return user.canEditHrComment
      ? detailsKeys.HR_COMMENT_EDIT
      : detailsKeys.HR_COMMENT_VIEW;
  }

  protected openHrComment(user: MonitoringUserSummaryExtended): void {
    if (!user) return;
    if (!user.hasHrComment && !user.canEditHrComment) return;
    this.onOpenHrComment.emit(user);
  }

  userCheckedChange(user: MonitoringUserSummaryExtended): void {
    const userSelected = this.allUsersData.find(p => p.userId === user.userId);
    if (userSelected) {
      userSelected.checked = !userSelected.checked;
      const ids = this.allUsersData
        .filter(p => p.checked)
        .map(p => p.userId);
      sessionStorage.setItem("userIdsChecked", ids.join(","));
    }
  }

  /**
     * Naviga alla matrice di calibrazione per l'utente selezionato
     * @param user L'utente selezionato
     */
  onGoToMatrix(user: MonitoringUserSummaryExtended): void {
    if (!user) return;
    void this.router.navigate([
      "/admin/calibration", this.processId, user.userId
    ]);
  }

  /**
   * Chiede l'apertura delle schede della persona in sola lettura (vista admin).
   *
   * Non naviga da qui: la pagina deve prima salvare filtri, ordinamento e paginazione, cosi' il
   * ritorno dal "Indietro" ritrova la lista com'era. Stesso giro della gestione lista feedback 360.
   *
   * @param user L'utente selezionato
   */
  goToEvaluationCards(user: MonitoringUserSummaryExtended): void {
    if (!user || !this.canViewEvaluationCards(user)) return;
    this.onGoToEvaluationCards.emit(user);
  }

  hasPendingFeedback360(user: MonitoringUserSummaryExtended): boolean {
    if (!user.hasManagerSubmittedFeedback360ToAdminApproval && user.scorecardType === "PROFESSIONAL_2026" && (user.feedback360Status === "RUNNING" || user.feedback360Status === "COMPLETED")) {
      return true;
    }
    return false;
  }

  hasListToValidateFeedback360(user: MonitoringUserSummaryExtended): boolean {
    if (user.hasManagerSubmittedFeedback360ToAdminApproval && user.hasFeedback360ToBeValidated && (user.feedback360Status === "RUNNING" || user.feedback360Status === "COMPLETED")) {
      return true;
    }
    return false;
  }

  withFeedback360Counters(user: MonitoringUserSummaryExtended): boolean {
    if ((user.feedback360Status === "RUNNING" || user.feedback360Status === "COMPLETED") && !this.hasListToValidateFeedback360(user) && !this.hasPendingFeedback360(user)) {
      return true;
    }
    return false;
  }

  goToHandleListFeedback360(user: MonitoringUserSummaryExtended): void {
    if (!user) return;
    if (!user.canImpersonateUser) {
      // se non posso impersonare l'utente, non posso neppure gestire la lista di feedback360
      return;
    }
    this.onGoToHandleListFeedback360.emit(user);
  }

  getBGColorHandleListButton(user: MonitoringUserSummaryExtended): string {
    if (user.hasFeedback360ToBeValidated || user.feedback360ToApproveCount) {
      return "black";
    }
    return "white";
  }

  /**
   * Emette l'evento per impersonare l'utente
   * @param user L'utente da impersonare
   */
  private onImpersonateUser(user: MonitoringUserSummaryExtended): void {
    if (!user) return;
    this.impersonateUser.emit(user);
  }

  /**
   * Emette l'evento per impersonare il manager
   * @param user L'utente il cui manager deve essere impersonato
   */
  private onImpersonateManager(user: MonitoringUserSummaryExtended): void {
    if (!user) return;
    this.impersonateManager.emit(user);
  }

  /**
   * Scarica la scheda di valutazione completa
   * @param user L'utente il cui la scheda di valutazione deve essere scaricata
   */
  private downloadEvaluationCard(user: MonitoringUserSummaryExtended): void {
    if (!user) return;
    this.onDownloadEvaluationCard.emit(user);
  }

  /**
   * Invia le schede di calibrazione
   * @param user L'utente il cui le schede di calibrazione devono essere inviate
   */
  private sendCalibrationCards(user: MonitoringUserSummaryExtended): void {
    if (!user) return;
    this.onSendCalibrationCards.emit(user);
  }
}
